import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageAccess, hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiPackage, FiPlus, FiTrendingUp } from "react-icons/fi";
import "./Inventory.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const unitLabel = (u) =>
  u === "cycle" ? "Çevrim" : u === "piece" ? "Parça" : u || "";

const resolveItemSource = (item) => {
  const part = item?.partId;
  const fromPart = part && typeof part === "object" ? part.source : undefined;
  return item?.source ?? fromPart;
};

const sourceLabel = (s) => {
  if (s === "purchased") return "Satın alınan";
  if (s === "mixed") return "Karma";
  if (s === "produced") return "Üretilen";
  if (s === undefined || s === null) return "—";
  return String(s);
};

/** Extra tokens so table search matches TR + EN (e.g. karma, mixed). */
const sourceSearchText = (s) => {
  if (s === "purchased") return "purchased satın alınan satin";
  if (s === "mixed") return "mixed karma";
  if (s === "produced") return "produced üretilen uretilen";
  return "";
};

const partUnitSymbol = (part) => {
  const u = part?.unitId;
  if (u && typeof u === "object") {
    return String(u.symbol || u.name || "").trim();
  }
  if (part?.unit) return unitLabel(part.unit);
  return "";
};

const equivalentUnitSymbol = (u) => {
  if (!u || typeof u !== "object") return "";
  return String(u.symbol || u.name || "").trim();
};

const formatStoredEquivalentValue = (raw) => {
  if (raw == null || raw === "") return "";
  const n = Number(raw);
  return Number.isFinite(n)
    ? Number.isInteger(n)
      ? String(n)
      : String(n)
    : String(raw);
};

/** Karşılık satırı — Part kaydındaki unitId, equivalentUnitId, equivalentValue. */
const partEquivalentFromDb = (part) => {
  if (!part || typeof part !== "object") {
    return { line: "", sortKey: "", status: "none", title: "" };
  }
  const primary = partUnitSymbol(part);
  const eqUnit = equivalentUnitSymbol(part.equivalentUnitId);
  const value = formatStoredEquivalentValue(part.equivalentValue);

  if (value && eqUnit && primary) {
    const line = `1 ${primary} = ${value} ${eqUnit}`;
    return {
      line,
      sortKey: line,
      status: "complete",
      title: `1 parça birimi (${primary}) = ${value} ${eqUnit}`,
    };
  }

  const partial = [primary, value, eqUnit].filter(Boolean);
  if (partial.length) {
    return {
      line: partial.join(" · "),
      sortKey: partial.join("-"),
      status: "partial",
      title: `Kayıtlı karşılık alanları: ${partial.join(", ")}`,
    };
  }

  if (part.isRawMaterial) {
    return {
      line: "",
      sortKey: "\uffff",
      status: "incomplete",
      title: "Hammadde — karşılık birimi veya değeri eksik",
    };
  }

  return { line: "", sortKey: "", status: "none", title: "" };
};

const formatWeightAmount = (value, unit) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const shown = Number.isInteger(n) ? String(n) : String(n);
  const u = String(unit || "").trim();
  return u ? `${shown} ${u}` : shown;
};

/** Karşılık: önce PartMachine (outputWeight), yoksa Part hammadde alanları. */
const inventoryKarşılıkDisplay = (item) => {
  const pm = item?.partMachineWeight;
  const part =
    item?.partId && typeof item.partId === "object" ? item.partId : null;

  if (
    pm &&
    pm.outputWeight != null &&
    Number.isFinite(Number(pm.outputWeight)) &&
    pm.weightUnit
  ) {
    const line = formatWeightAmount(pm.outputWeight, pm.weightUnit);
    const w = Math.max(0, Number(pm.wastage) || 0);
    const cq = Math.max(1, Number(pm.cycleQuantity) || 1);
    const extra =
      w > 0 ? ` · fire ${formatWeightAmount(w, pm.weightUnit)}` : "";
    return {
      line: `${line}${extra}`,
      sortKey: line,
      status: "complete",
      source: "partMachine",
      title: `Makine ataması — çıktı: ${line}, çevrim: ${cq}${w > 0 ? `, fire: ${formatWeightAmount(w, pm.weightUnit)}` : ""}`,
    };
  }

  const fromPart = partEquivalentFromDb(part);
  if (fromPart.status !== "none") {
    return { ...fromPart, source: "part" };
  }
  return fromPart;
};

const inventoryKarşılıkSortKey = (item) =>
  inventoryKarşılıkDisplay(item).sortKey;

const partEquivalentSortKey = (part) => partEquivalentFromDb(part).sortKey;

/** @deprecated — kept for hot-reload / stale refs */
const rawMaterialEquivalentLabel = (part) => partEquivalentFromDb(part).line;
const rawMaterialSortKey = (part) => partEquivalentSortKey(part);

const isStockEntryAllowedSource = (v) =>
  v === undefined || v === null || v === "purchased" || v === "mixed";

const partSource = (item) =>
  item?.partId && typeof item.partId === "object"
    ? item.partId.source
    : undefined;

const canStockEntryActions = (item) =>
  isStockEntryAllowedSource(item?.source) ||
  isStockEntryAllowedSource(partSource(item));

const emptyForm = () => ({
  partId: "",
  notes: "",
});

const headers = [
  {
    name: "Parça Kodu",
    dataType: "text",
    id: 0,
    customSorter: (a, b) =>
      String(a.partId?.code || a.code || "").localeCompare(
        String(b.partId?.code || b.code || ""),
      ),
  },
  {
    name: "Parça Adı",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a.partId?.name || a.name || "").localeCompare(
        String(b.partId?.name || b.name || ""),
      ),
  },
  {
    name: "Birim",
    id: 2,
    sortable: false,
  },
  {
    name: "Kaynak",
    dataType: "text",
    id: 3,
    customSorter: (a, b) =>
      String(sourceLabel(resolveItemSource(a))).localeCompare(
        String(sourceLabel(resolveItemSource(b))),
      ),
  },
  {
    name: "Karşılık",
    dataType: "text",
    id: 7,
    customSorter: (a, b) =>
      inventoryKarşılıkSortKey(a).localeCompare(inventoryKarşılıkSortKey(b)),
  },
  {
    name: "Stok",
    dataType: "number",
    id: 4,
    customSorter: (a, b) =>
      (Number(a.quantity) || 0) - (Number(b.quantity) || 0),
  },
  {
    name: "Durum",
    dataType: "text",
    id: 5,
    customSorter: (a, b) =>
      String(a.partId?.isActive ? "active" : "inactive").localeCompare(
        String(b.partId?.isActive ? "active" : "inactive"),
      ),
  },
  { name: "İşlemler", id: 6, sortable: false },
];

const Inventory = () => {
  useTitle("Rovion MES - Envanter");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/inventory",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const canAddInventory =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/inventory/add",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const canViewAdditions =
    !!currentUser &&
    hasPageAccess(
      currentUser.role,
      [],
      "/inventory/additions",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("inventory-list", headers);

  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [movementsModal, setMovementsModal] = useState({
    isOpen: false,
    item: null,
  });
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movements, setMovements] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const addToast = useCallback((title, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, title, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3000,
    );
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/inventory`, authH());
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      addToast("Envanter yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authH, addToast]);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/parts`, authH());
      setParts(res.data?.parts || []);
    } catch {}
  }, [authH]);

  useEffect(() => {
    fetchItems();
    fetchParts();
  }, [fetchItems, fetchParts]);

  const openMovements = async (item) => {
    setMovementsModal({ isOpen: false, item });
    setMovementsLoading(true);
    setMovements([]);
    setMovementsTotal(0);
    try {
      const res = await axios.get(`${API_URL}/api/stock-movements`, {
        ...authH(),
        params: { inventoryItemId: item?._id, limit: 200, skip: 0 },
      });
      setMovements(
        Array.isArray(res.data?.movements) ? res.data.movements : [],
      );
      setMovementsTotal(Number(res.data?.total) || 0);
    } catch (err) {
      addToast(
        err?.response?.data?.message || "Stok hareketleri alınamadı",
        "danger",
      );
    } finally {
      setMovementsLoading(false);
    }
  };

  const closeMovements = () => {
    setMovementsModal({ isOpen: false, item: null });
    setMovements([]);
    setMovementsTotal(0);
    setMovementsLoading(false);
  };

  const movementTypeLabel = (t) =>
    t === "addition"
      ? "Giriş"
      : t === "consumption"
        ? "Tüketim"
        : t === "production"
          ? "Üretim"
          : t === "adjustment"
            ? "Düzeltme"
            : t || "—";

  const fmtDateTime = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  };

  return (
    <AppPage
      title="Envanter"
      actions={
        canWrite || canAddInventory || canViewAdditions ? (
          <div className="d-flex gap-2 flex-wrap">
            {canViewAdditions && (
              <Button
                appearance="default"
                onClick={() => navigate("/inventory/additions")}
              >
                Giriş kayıtları
              </Button>
            )}
            {canViewAdditions && (
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/inventory/add")}
              >
                Envnater Kaydı Oluştur
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.title}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>

      <CardContainer>
        <Table
          columns={headers}
          visibleColumns={visibleColumns}
          tableId="inventory-list"
          loading={loading}
          search={true}
          searchPlaceholder="Parça, kod, kaynak (ör. karma, satın alınan)..."
          viewOptions={
            <ListViewOptionsPanel
              columns={headers}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {items.map((item) => {
            const part = item.partId;
            const source = resolveItemSource(item);
            return (
              <Tr key={item._id} data={item}>
                {visibleColumns.includes(0) && (
                <Td>
                  <span
                    role="button"
                    tabIndex={0}
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      background: "var(--m-badge-default-subtle-bg, #f1f5f9)",
                      padding: "2px 6px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    onClick={() => openMovements(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        openMovements(item);
                    }}
                    title="Stok hareketlerini görüntüle"
                  >
                    {part?.code || item.code || "—"}
                  </span>
                </Td>
                )}
                {visibleColumns.includes(1) && (
                <Td>
                  <strong
                    role="button"
                    tabIndex={0}
                    style={{ fontSize: "0.875rem", cursor: "pointer" }}
                    onClick={() => openMovements(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        openMovements(item);
                    }}
                    title="Stok hareketlerini görüntüle"
                  >
                    {part?.name || item.name || "—"}
                  </strong>
                </Td>
                )}
                {visibleColumns.includes(2) && (
                <Td>
                  <span className="text-muted small">
                    {partUnitSymbol(part) || "—"}
                  </span>
                </Td>
                )}
                {visibleColumns.includes(3) && (
                <Td>
                  <span className="text-muted small">
                    {sourceLabel(source)}
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: "hidden",
                      clip: "rect(0,0,0,0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  >
                    {sourceSearchText(source)}
                  </span>
                </Td>
                )}
                {visibleColumns.includes(7) && (
                <Td>
                  {(() => {
                    const info = inventoryKarşılıkDisplay(item);
                    if (info.status === "none") {
                      return <span className="text-muted small">—</span>;
                    }
                    return (
                      <span
                        className="d-inline-flex align-items-center gap-1"
                        style={{ maxWidth: 180, minWidth: 0 }}
                        title={info.title}
                      >
                        {info.line ? (
                          <span
                            className="text-truncate d-inline-block"
                            style={{
                              fontFamily: "monospace",
                              fontSize: "0.7rem",
                              color:
                                info.status === "complete"
                                  ? info.source === "partMachine"
                                    ? "#2563eb"
                                    : "#b45309"
                                  : "#64748b",
                              lineHeight: 1.2,
                              maxWidth: 140,
                              verticalAlign: "middle",
                            }}
                          >
                            {info.line}
                          </span>
                        ) : (
                          <span
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            …
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </Td>
                )}
                {visibleColumns.includes(4) && (
                <Td>
                  <div
                    className="d-flex align-items-center gap-2 flex-wrap"
                    title={
                      Number(item.quantity) < 0
                        ? "Stok sıfırın altında (üretim / tüketim sonrası)"
                        : undefined
                    }
                  >
                    <span
                      className={`fw-semibold${Number(item.quantity) < 0 ? " text-danger" : ""}`}
                    >
                      {item.quantity ?? 0}
                    </span>
                    {Number(item.quantity) < 0 && (
                      <Lozenge appearance="danger-subtle">
                        İşlem Gerekli
                      </Lozenge>
                    )}
                  </div>
                </Td>
                )}
                {visibleColumns.includes(5) && (
                <Td>
                  <Lozenge
                    appearance={
                      part?.isActive === false
                        ? "danger-subtle"
                        : "success-subtle"
                    }
                  >
                    {part?.isActive === false ? "Pasif" : "Aktif"}
                  </Lozenge>
                </Td>
                )}
                {visibleColumns.includes(6) && (
                <Td>
                  <div className="d-flex gap-2">
                    {canStockEntryActions(item) && !!part?._id && (
                      <IconButton
                        icon={<FiTrendingUp />}
                        label="Envanter girişleri"
                        title="Envanter girişlerini görüntüle"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          navigate(`/parts/${part._id}/stock-additions`)
                        }
                      />
                    )}
                    {canStockEntryActions(item) && canAddInventory && (
                      <IconButton
                        icon={<FiPlus />}
                        label="Envanter girişi ekle"
                        title="Envanter girişi ekle"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          navigate(`/inventory/add?itemId=${item._id}`)
                        }
                      />
                    )}
                  </div>
                </Td>
                )}
              </Tr>
            );
          })}
        </Table>
        {!loading && items.length === 0 && (
          <div className="empty-state">
            <FiPackage size={64} />
            <p>Envanter kalemi bulunamadı</p>
            {canAddInventory && (
              <Button
                className="mt-2"
                appearance="primary"
                onClick={() => navigate("/inventory/add")}
                iconBefore={<FiPlus />}
              >
                Envanter girişi yap
              </Button>
            )}
          </div>
        )}
      </CardContainer>

      <Modal
        isOpen={movementsModal.isOpen}
        onClose={closeMovements}
        width="large"
      >
        <ModalHeader>
          Stok hareketleri{" "}
          <span className="text-muted">
            {movementsModal.item?.partId?.code
              ? `(${movementsModal.item.partId.code})`
              : ""}
          </span>
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div className="text-muted small">
              {movementsLoading
                ? "Yükleniyor..."
                : `${movements.length} kayıt gösteriliyor${movementsTotal > movements.length ? ` / toplam ${movementsTotal}` : ""}`}
            </div>
            <Button
              appearance="subtle"
              onClick={() =>
                movementsModal.item && openMovements(movementsModal.item)
              }
              disabled={movementsLoading}
            >
              Yenile
            </Button>
          </div>

          {!movementsLoading && movements.length === 0 ? (
            <div className="text-muted">Kayıt bulunamadı.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th className="text-end">Δ</th>
                    <th className="text-end">Önce</th>
                    <th className="text-end">Sonra</th>
                    <th>Kullanıcı</th>
                    <th>Referans</th>
                    <th>Not</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m._id}>
                      <td>{fmtDateTime(m.createdAt)}</td>
                      <td>{movementTypeLabel(m.type)}</td>
                      <td
                        className="text-end"
                        style={{ fontFamily: "monospace" }}
                      >
                        {Number(m.qtyDelta) > 0
                          ? `+${m.qtyDelta}`
                          : (m.qtyDelta ?? "—")}
                      </td>
                      <td
                        className="text-end"
                        style={{ fontFamily: "monospace" }}
                      >
                        {m.beforeQty ?? "—"}
                      </td>
                      <td
                        className="text-end"
                        style={{ fontFamily: "monospace" }}
                      >
                        {m.afterQty ?? "—"}
                      </td>
                      <td>{m.userId?.name || "—"}</td>
                      <td style={{ fontFamily: "monospace" }}>
                        {m.refType
                          ? `${m.refType}${m.refId ? `:${String(m.refId).slice(-6)}` : ""}`
                          : "—"}
                      </td>
                      <td>{m.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={closeMovements}>
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(Inventory, ["admin", "employee", "user"]);
